import { useCallback, useEffect, useState } from 'react';

import { Assessment, Lessons } from 'modules/dashboard/core/type';

import { trimValue } from 'helpers';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce } from 'lodash';

export const useCommonController = (institutionId: string) => {
  const [assessmentData, setAssessmentData] = useState<Assessment[]>([]);

  const [lessonData, setLessonData] = useState<Lessons[]>([]);

  const [pageLesson, setPageLesson] = useState<number>(1);

  const [pageAssessment, setPageAssessment] = useState<number>(1);

  const hasMoreLesson = useBoolean(false);

  const hasMoreAssessment = useBoolean(false);

  const totalPageLesson = useNumber(0);

  const totalPageAssessment = useNumber(0);

  const isLoading = useBoolean(false);

  const searchLesson = useString('');

  const searchAssessment = useString('');

  useEffect(() => {
    if (!institutionId || pageLesson < 1) {
      return;
    }

    getListLesson();
  }, [institutionId, pageLesson]);

  useEffect(() => {
    if (!institutionId || pageAssessment < 1) {
      return;
    }

    getListAssessment();
  }, [institutionId, pageAssessment]);

  const getListLesson = async (search: string = '') => {
    const result: { items: Lessons[]; total: number } = await apiCall({
      url: `/lessons`,
      method: 'GET',
      data: {
        page: pageLesson.toString(),
        limit: 10,
        institutionId,
        search,
      },
    });

    hasMoreLesson.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items ?? [];

    if (pageLesson < 1) return;

    if (pageLesson === 1) {
      setLessonData(newItems);
      totalPageLesson.setValue(result.total);

      return;
    }

    setLessonData((prev) => [...prev, ...newItems]);
  };

  const getListAssessment = async (search: string = '') => {
    const result: {
      items: Assessment[];
      total: number;
    } = await apiCall({
      url: '/assessments',
      method: 'GET',
      data: {
        page: pageAssessment.toString(),
        limit: 10,
        institutionId,
        search: search,
      },
    });

    hasMoreAssessment.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    const newItems = result?.items?.length ? result.items.filter((el) => el.isClass) : [];

    if (pageAssessment < 1) return;

    if (pageAssessment === 1) {
      setAssessmentData(newItems);
      totalPageAssessment.setValue(result.total);

      return;
    }

    setAssessmentData((prev) => [...prev, ...newItems]);
  };

  const handleLoadMoreLesson = () => {
    if (!hasMoreLesson.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageLesson((prev) => prev + 1);
    }, 1200);
  };

  const handleLoadMoreAssessment = () => {
    if (!hasMoreAssessment.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageAssessment((prev) => prev + 1);
    }, 1200);
  };

  const onChangeSearchLesson = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchLesson.setValue(event.target.value);
    debounceSearchLesson(event.target.value);
  };

  const debounceSearchLesson = useCallback(
    debounce((newSearch: string) => {
      getListLesson(trimValue(newSearch));
    }, 500),
    [],
  );

  const onChangeSearchAssessment = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchAssessment.setValue(event.target.value);
    debounceSearchAssessment(event.target.value);
  };

  const debounceSearchAssessment = useCallback(
    debounce((newSearch: string) => {
      getListAssessment(trimValue(newSearch));
    }, 500),
    [],
  );

  return {
    hasMoreLesson,
    hasMoreAssessment,
    lessonData,
    assessmentData,
    handleLoadMoreLesson,
    handleLoadMoreAssessment,
    onChangeSearchLesson,
    onChangeSearchAssessment,
    searchAssessment,
    searchLesson,
  };
};
